import { useEffect,useState } from "react";
import { Box,Button,CircularProgress,Dialog,DialogActions,DialogContent,DialogTitle,Typography } from "@mui/material";
import { useAppTheme } from "../../../context/ThemeContext";
import { useAlert } from "../../../context/AlertContext";

const readResponse=async response=>{
  const contentType=response.headers.get("content-type")||"";

  if(contentType.includes("application/json")){
    return response.json();
  }

  if(!response.ok){
    throw new Error("El servidor no respondió correctamente.");
  }

  return {};
};

export default function Carnets({open,player,onClose}){
  const {colors,typography,shape}=useAppTheme();
  const {showError}=useAlert();

  const [loading,setLoading]=useState(false);
  const [data,setData]=useState(null);

  useEffect(()=>{
    if(!open||!player?.slug){
      setData(null);
      return;
    }

    let active=true;

    const load=async()=>{
      try{
        setLoading(true);

        const response=await fetch(
          `/competition/delegate/players/${player.slug}/carnet/?output=data`,
          {credentials:"include"}
        );

        const result=await readResponse(response);

        if(!response.ok){
          throw new Error(
            result.error||
            result.detail||
            "No se pudo generar el carnet."
          );
        }

        if(active){
          setData(result);
        }
      }catch(error){
        if(active){
          showError(
            error.message||
            "No se pudo generar el carnet."
          );
        }
      }finally{
        if(active){
          setLoading(false);
        }
      }
    };

    load();

    return ()=>{
      active=false;
    };
  },[open,player?.slug]);

  const carnet=data?.player;
  const qr=data?.qr;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      disableScrollLock
      PaperProps={{
        sx:{
          borderRadius:shape.radiusLarge,
          boxShadow:"none",
          border:`1px solid ${colors.border}`,
          backgroundColor:colors.surface
        }
      }}
    >
      <DialogTitle>
        <Typography
          sx={{
            fontSize:typography.sizes.xl,
            fontWeight:700,
            color:colors.text
          }}
        >
          Carnet del jugador
        </Typography>

        <Typography
          sx={{
            mt:0.3,
            fontSize:typography.sizes.sm,
            color:colors.textSecondary
          }}
        >
          Vista previa de la identificación deportiva del jugador.
        </Typography>
      </DialogTitle>

      <DialogContent>
        {loading?(
          <Box
            sx={{
              height:300,
              display:"flex",
              alignItems:"center",
              justifyContent:"center"
            }}
          >
            <CircularProgress
              size={26}
              sx={{color:colors.secondary}}
            />
          </Box>
        ):carnet&&qr?(
          <Box
            sx={{
              display:"flex",
              justifyContent:"center",
              py:2,
              overflowX:"auto"
            }}
          >
            <Box
              sx={{
                width:600,
                height:379,
                minWidth:600,
                border:`1px solid ${colors.border}`,
                borderRadius:shape.radiusLarge,
                backgroundColor:colors.surface,
                overflow:"hidden",
                display:"flex",
                flexDirection:"column"
              }}
            >
              <Box
                sx={{
                  height:84,
                  px:2.5,
                  display:"flex",
                  alignItems:"center",
                  backgroundColor:colors.primary
                }}
              >
                {carnet.team?.logo&&(
                  <Box
                    component="img"
                    src={carnet.team.logo}
                    alt={carnet.team.name}
                    sx={{
                      width:56,
                      height:56,
                      objectFit:"contain",
                      flexShrink:0
                    }}
                  />
                )}

                <Box
                  sx={{
                    ml:carnet.team?.logo?1.5:0,
                    minWidth:0
                  }}
                >
                  <Typography
                    sx={{
                      fontSize:typography.sizes.lg,
                      fontWeight:700,
                      color:colors.tableHeaderText,
                      lineHeight:1.15
                    }}
                  >
                    {carnet.team?.name}
                  </Typography>

                  <Typography
                    sx={{
                      mt:0.5,
                      fontSize:typography.sizes.xs,
                      color:colors.tableHeaderText,
                      opacity:0.72,
                      letterSpacing:"0.06em"
                    }}
                  >
                    CARNET DE JUGADOR
                  </Typography>
                </Box>
              </Box>

              <Box
                sx={{
                  flex:1,
                  p:2,
                  display:"grid",
                  gridTemplateColumns:"176px 1fr 126px",
                  gap:2
                }}
              >
                <Box
                  sx={{
                    width:176,
                    height:238,
                    borderRadius:shape.radius,
                    overflow:"hidden",
                    backgroundColor:colors.backgroundSoft,
                    border:`1px solid ${colors.border}`
                  }}
                >
                  {carnet.photo?(
                    <Box
                      component="img"
                      src={carnet.photo}
                      alt={carnet.name}
                      sx={{
                        width:"100%",
                        height:"100%",
                        display:"block",
                        objectFit:"cover"
                      }}
                    />
                  ):(
                    <Box
                      sx={{
                        height:"100%",
                        display:"flex",
                        alignItems:"center",
                        justifyContent:"center"
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize:typography.sizes.sm,
                          color:colors.textMuted
                        }}
                      >
                        Sin fotografía
                      </Typography>
                    </Box>
                  )}
                </Box>

                <Box
                  sx={{
                    minWidth:0,
                    pt:0.5
                  }}
                >
                  <Typography
                    sx={{
                      fontSize:typography.sizes.xs,
                      fontWeight:600,
                      color:colors.textMuted,
                      letterSpacing:"0.05em"
                    }}
                  >
                    JUGADOR
                  </Typography>

                  <Typography
                    sx={{
                      mt:0.5,
                      fontSize:typography.sizes.xl,
                      fontWeight:700,
                      color:colors.text,
                      lineHeight:1.2
                    }}
                  >
                    {carnet.name}
                  </Typography>

                  <Box
                    sx={{
                      my:2,
                      borderTop:`1px solid ${colors.border}`
                    }}
                  />

                  <Typography
                    sx={{
                      fontSize:typography.sizes.xs,
                      fontWeight:600,
                      color:colors.textMuted,
                      letterSpacing:"0.05em"
                    }}
                  >
                    DORSAL
                  </Typography>

                  <Typography
                    sx={{
                      mt:0.5,
                      fontSize:"2rem",
                      fontWeight:700,
                      color:colors.text,
                      lineHeight:1
                    }}
                  >
                    {carnet.jersey_number??"—"}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display:"flex",
                    flexDirection:"column",
                    alignItems:"center",
                    justifyContent:"center"
                  }}
                >
                  <Box
                    sx={{
                      mb:1.5,
                      px:1.5,
                      py:0.6,
                      borderRadius:shape.radiusSmall,
                      backgroundColor:colors.secondary
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize:"0.64rem",
                        fontWeight:600,
                        color:colors.buttonPrimaryText,
                        letterSpacing:"0.04em"
                      }}
                    >
                      QR JUGADOR
                    </Typography>
                  </Box>

                  <Box
                    component="img"
                    src={qr.image}
                    alt="QR del jugador"
                    sx={{
                      width:116,
                      height:116,
                      objectFit:"contain"
                    }}
                  />
                </Box>
              </Box>

              <Box
                sx={{
                  height:12,
                  backgroundColor:colors.secondary
                }}
              />
            </Box>
          </Box>
        ):(
          <Box
            sx={{
              py:5,
              textAlign:"center"
            }}
          >
            <Typography
              sx={{
                fontSize:typography.sizes.md,
                color:colors.textSecondary
              }}
            >
              No se pudo mostrar el carnet.
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px:3,
          py:2,
          borderTop:`1px solid ${colors.border}`
        }}
      >
        <Button
          size="small"
          onClick={onClose}
          sx={{
            color:colors.buttonText,
            textTransform:"none"
          }}
        >
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
}